/**
 * WcagAuditPanel — admin tile for the external WCAG 2.2 AA audit cadence.
 *
 * The Accessibility Statement at `/accessibility` publicly commits to a
 * 365-day external audit cycle. This tile is where the admin records
 * the close of each audit so the backend cron knows when the next one
 * is due and stops emailing reminders for the current cycle.
 *
 * Read: GET /api/admin/ops/wcag-audit
 *   → { last_audit_at, next_audit_at, cadence_days, lead_days,
 *       auditor, report_url }
 * Write: POST /api/admin/ops/wcag-audit
 *   body: { audit_date: "YYYY-MM-DD", auditor?, report_url? }
 */
import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Calendar, RefreshCw, ExternalLink, Zap, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
    adminWcagAuditGet,
    adminWcagAuditUpdate,
    adminWcagStaticAuditRun,
    adminWcagStaticAuditLatest,
} from "@/lib/api";

function _fmtDate(s) {
    if (!s) return "—";
    try {
        const d = new Date(s);
        if (isNaN(d.getTime())) return s;
        return d.toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch {
        return s;
    }
}

function _daysUntil(iso) {
    if (!iso) return null;
    try {
        const due = new Date(iso).getTime();
        if (!due) return null;
        return Math.round((due - Date.now()) / (1000 * 60 * 60 * 24));
    } catch {
        return null;
    }
}

export default function WcagAuditPanel() {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [auditDate, setAuditDate] = useState("");
    const [auditor, setAuditor] = useState("");
    const [reportUrl, setReportUrl] = useState("");
    // Automated scanner state
    const [scanLatest, setScanLatest] = useState(null);
    const [scanHistory, setScanHistory] = useState([]);
    const [scanLoading, setScanLoading] = useState(false);
    const [scanRunning, setScanRunning] = useState(false);
    const [showScanDetail, setShowScanDetail] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const data = await adminWcagAuditGet();
            setState(data);
            setAuditDate((data?.last_audit_at || "").slice(0, 10) || "");
            setAuditor(data?.auditor || "");
            setReportUrl(data?.report_url || "");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || e?.message || "Could not load WCAG audit state.",
            );
        } finally {
            setLoading(false);
        }
    };

    const loadScanHistory = async () => {
        setScanLoading(true);
        try {
            const data = await adminWcagStaticAuditLatest();
            setScanLatest(data?.latest || null);
            setScanHistory(data?.history || []);
        } catch (e) {
            // Non-fatal — scanner may have never run yet.
            setScanLatest(null);
            setScanHistory([]);
        } finally {
            setScanLoading(false);
        }
    };

    useEffect(() => {
        load();
        loadScanHistory();
    }, []);

    const runScan = async () => {
        setScanRunning(true);
        try {
            const data = await adminWcagStaticAuditRun();
            setScanLatest(data);
            const s = data?.summary || {};
            const fail = s.fail || 0;
            const warn = s.warn || 0;
            if (fail > 0) {
                toast.error(`Scan complete — ${fail} failing route(s).`);
            } else if (warn > 0) {
                toast.warning(`Scan complete — ${warn} warning(s).`);
            } else {
                toast.success(`Scan complete — all ${s.total || 0} routes pass.`);
            }
            await loadScanHistory();
            setShowScanDetail(true);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || e?.message || "Scan failed.",
            );
        } finally {
            setScanRunning(false);
        }
    };

    const save = async () => {
        if (!auditDate) {
            toast.error("Pick the audit close date.");
            return;
        }
        setSaving(true);
        try {
            const data = await adminWcagAuditUpdate({
                audit_date: auditDate,
                auditor: auditor || null,
                report_url: reportUrl || null,
            });
            setState(data);
            toast.success("Audit cycle updated.");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || e?.message || "Update failed.",
            );
        } finally {
            setSaving(false);
        }
    };

    const days = _daysUntil(state?.next_audit_at);
    let badge = { label: "No audit recorded", tone: "muted" };
    if (state?.last_audit_at && days !== null) {
        if (days < 0) {
            badge = { label: `Overdue by ${Math.abs(days)}d`, tone: "rust" };
        } else if (days <= (state?.lead_days ?? 30)) {
            badge = { label: `Due in ${days}d`, tone: "amber" };
        } else {
            badge = { label: `Due in ${days}d`, tone: "moss" };
        }
    }
    const toneClass = {
        moss: "bg-[var(--bl-moss-tint)] text-[var(--bl-moss)]",
        amber: "bg-[var(--bl-amber-tint)] text-[var(--bl-amber)]",
        rust: "bg-[var(--bl-rust-tint)] text-[var(--bl-rust)]",
        muted: "bg-bl-surface text-bl-muted",
    }[badge.tone];

    return (
        <section
            className="bl-card p-6 sm:p-8"
            data-testid="wcag-audit-panel"
        >
            <div className="flex items-start gap-3 mb-5">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[var(--bl-tint)] text-bl-primary shrink-0">
                    <ShieldCheck className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="font-display font-semibold text-xl text-bl">
                            External Accessibility Audit
                        </h2>
                        <span
                            className={`text-[11px] font-medium uppercase tracking-wide px-2 py-1 rounded-full ${toneClass}`}
                            data-testid="wcag-audit-badge"
                        >
                            {badge.label}
                        </span>
                    </div>
                    <p className="mt-1 text-sm text-bl-muted leading-relaxed max-w-xl">
                        Committed in the public Accessibility Statement —
                        a 365-day cadence with a {state?.lead_days ?? 30}-day
                        lead-time reminder. Record each audit close so the
                        cron knows when to nudge again.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="bl-btn-secondary text-sm gap-1.5"
                    data-testid="wcag-audit-reload"
                >
                    {loading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                        <RefreshCw className="w-3.5 h-3.5" />
                    )}
                    Reload
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                <div
                    className="rounded-lg border border-bl bg-bl-surface p-3"
                    data-testid="wcag-audit-last"
                >
                    <div className="text-xs uppercase tracking-wide text-bl-muted">
                        Last close
                    </div>
                    <div className="mt-1 font-display font-semibold text-bl">
                        {_fmtDate(state?.last_audit_at)}
                    </div>
                </div>
                <div
                    className="rounded-lg border border-bl bg-bl-surface p-3"
                    data-testid="wcag-audit-next"
                >
                    <div className="text-xs uppercase tracking-wide text-bl-muted">
                        Next due
                    </div>
                    <div className="mt-1 font-display font-semibold text-bl">
                        {_fmtDate(state?.next_audit_at)}
                    </div>
                </div>
                <div
                    className="rounded-lg border border-bl bg-bl-surface p-3"
                    data-testid="wcag-audit-cadence"
                >
                    <div className="text-xs uppercase tracking-wide text-bl-muted">
                        Cadence
                    </div>
                    <div className="mt-1 font-display font-semibold text-bl">
                        {state?.cadence_days ?? 365} days
                    </div>
                </div>
            </div>

            {state?.report_url ? (
                <a
                    href={state.report_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-bl-primary hover:underline mb-4"
                    data-testid="wcag-audit-report-link"
                >
                    Open most recent report
                    <ExternalLink className="w-3.5 h-3.5" />
                </a>
            ) : null}

            <div className="rounded-lg border border-bl bg-bl-surface p-4">
                <h3 className="font-display font-semibold text-bl mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-bl-muted" />
                    Record audit close
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className="block">
                        <span className="text-xs text-bl-muted">Audit close date</span>
                        <input
                            type="date"
                            value={auditDate}
                            onChange={(e) => setAuditDate(e.target.value)}
                            className="bl-input mt-1 w-full text-sm"
                            data-testid="wcag-audit-date-input"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs text-bl-muted">Auditor (optional)</span>
                        <input
                            type="text"
                            value={auditor}
                            onChange={(e) => setAuditor(e.target.value)}
                            placeholder="e.g. Deque Systems"
                            className="bl-input mt-1 w-full text-sm"
                            data-testid="wcag-audit-auditor-input"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs text-bl-muted">Report URL (optional)</span>
                        <input
                            type="url"
                            value={reportUrl}
                            onChange={(e) => setReportUrl(e.target.value)}
                            placeholder="https://…"
                            className="bl-input mt-1 w-full text-sm"
                            data-testid="wcag-audit-report-input"
                        />
                    </label>
                </div>
                <div className="mt-3 flex justify-end">
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || !auditDate}
                        className="bl-btn-primary text-sm gap-1.5"
                        data-testid="wcag-audit-save-btn"
                    >
                        {saving ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <ShieldCheck className="w-3.5 h-3.5" />
                        )}
                        Save cycle
                    </button>
                </div>
            </div>

            {/* ============================================================
                Automated WCAG 2.2 AA scanner — runs between annual audits
                ============================================================ */}
            <div
                className="mt-6 rounded-lg border border-bl bg-bl-surface p-4"
                data-testid="wcag-static-audit-section"
            >
                <div className="flex items-start gap-3 mb-3">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--bl-tint)] text-bl-primary shrink-0">
                        <Zap className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-display font-semibold text-bl">
                            Automated WCAG scan
                        </h3>
                        <p className="text-sm text-bl-muted mt-0.5 leading-relaxed">
                            Static rule-based audit across {scanLatest?.summary?.total || 13} public
                            routes. Catches regressions between annual external audits.
                            Weekly cron + on-demand.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={runScan}
                        disabled={scanRunning}
                        className="bl-btn-primary text-sm gap-1.5"
                        data-testid="wcag-static-audit-run-btn"
                    >
                        {scanRunning ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <Zap className="w-3.5 h-3.5" />
                        )}
                        Run scan now
                    </button>
                </div>

                {scanLatest ? (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div
                                className="rounded-lg border border-bl bg-white p-3"
                                data-testid="wcag-static-pass-tile"
                            >
                                <div className="text-xs uppercase tracking-wide text-bl-muted">Pass</div>
                                <div className="mt-1 font-display font-semibold text-2xl text-[var(--bl-moss)]">
                                    {scanLatest?.summary?.pass ?? 0}
                                </div>
                            </div>
                            <div
                                className="rounded-lg border border-bl bg-white p-3"
                                data-testid="wcag-static-warn-tile"
                            >
                                <div className="text-xs uppercase tracking-wide text-bl-muted">Warn</div>
                                <div className="mt-1 font-display font-semibold text-2xl text-[var(--bl-amber)]">
                                    {scanLatest?.summary?.warn ?? 0}
                                </div>
                            </div>
                            <div
                                className="rounded-lg border border-bl bg-white p-3"
                                data-testid="wcag-static-fail-tile"
                            >
                                <div className="text-xs uppercase tracking-wide text-bl-muted">Fail</div>
                                <div className="mt-1 font-display font-semibold text-2xl text-[var(--bl-rust)]">
                                    {scanLatest?.summary?.fail ?? 0}
                                </div>
                            </div>
                            <div className="rounded-lg border border-bl bg-white p-3">
                                <div className="text-xs uppercase tracking-wide text-bl-muted">Last run</div>
                                <div className="mt-1 text-sm text-bl">
                                    {scanLatest?.started_at
                                        ? new Date(scanLatest.started_at).toLocaleString()
                                        : "—"}
                                </div>
                            </div>
                        </div>

                        {/* 12-run sparkline */}
                        {scanHistory && scanHistory.length > 0 ? (
                            <div
                                className="mt-3 flex items-center gap-1"
                                data-testid="wcag-static-sparkline"
                            >
                                <span className="text-xs text-bl-muted mr-2">
                                    Last {scanHistory.length} runs:
                                </span>
                                {scanHistory.slice().reverse().map((h, i) => {
                                    const s = h?.summary || {};
                                    const tone = s.fail > 0
                                        ? "bg-[var(--bl-rust)]"
                                        : s.warn > 0
                                            ? "bg-[var(--bl-amber)]"
                                            : "bg-[var(--bl-moss)]";
                                    const stamp = h?.started_at
                                        ? new Date(h.started_at).toLocaleDateString()
                                        : "";
                                    return (
                                        <span
                                            key={`${stamp}-${i}`}
                                            title={`${stamp} · ${s.fail || 0} fail · ${s.warn || 0} warn · ${s.pass || 0} pass`}
                                            className={`inline-block w-3 h-5 rounded-sm ${tone}`}
                                        />
                                    );
                                })}
                            </div>
                        ) : null}

                        <button
                            type="button"
                            onClick={() => setShowScanDetail((v) => !v)}
                            className="mt-3 text-sm text-bl-primary hover:underline"
                            data-testid="wcag-static-toggle-detail"
                        >
                            {showScanDetail ? "Hide per-route detail" : "Show per-route detail"}
                        </button>

                        {showScanDetail && Array.isArray(scanLatest?.results) ? (
                            <div
                                className="mt-3 space-y-2"
                                data-testid="wcag-static-detail-list"
                            >
                                {scanLatest.results.map((r) => {
                                    const tone = r.status === "fail"
                                        ? "border-[var(--bl-rust)] bg-[var(--bl-rust-tint)]"
                                        : r.status === "warn"
                                            ? "border-[var(--bl-amber)] bg-[var(--bl-amber-tint)]"
                                            : "border-bl bg-white";
                                    const Icon = r.status === "pass" ? CheckCircle2 : AlertTriangle;
                                    const iconColor = r.status === "pass"
                                        ? "text-[var(--bl-moss)]"
                                        : r.status === "warn"
                                            ? "text-[var(--bl-amber)]"
                                            : "text-[var(--bl-rust)]";
                                    return (
                                        <details
                                            key={r.route}
                                            className={`rounded border ${tone} p-2`}
                                            data-testid={`wcag-static-route-${r.route.replace(/[^a-z0-9]+/gi, "-")}`}
                                        >
                                            <summary className="cursor-pointer text-sm flex items-center gap-2">
                                                <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
                                                <span className="font-mono">{r.route}</span>
                                                <span className="text-xs text-bl-muted ml-auto">
                                                    {r.issue_count || 0} issue · {r.warning_count || 0} warn
                                                </span>
                                            </summary>
                                            {(r.issues || []).length + (r.warnings || []).length > 0 ? (
                                                <ul className="mt-2 pl-6 space-y-1 text-xs text-bl-muted">
                                                    {(r.issues || []).map((iss, j) => (
                                                        <li key={`i-${j}`}>
                                                            <span className="text-[var(--bl-rust)] font-semibold">
                                                                {iss.rule}
                                                            </span>
                                                            {" — "}
                                                            {iss.title}
                                                            {iss.detail ? ` · ${iss.detail}` : ""}
                                                        </li>
                                                    ))}
                                                    {(r.warnings || []).map((w, j) => (
                                                        <li key={`w-${j}`}>
                                                            <span className="text-[var(--bl-amber)] font-semibold">
                                                                {w.rule}
                                                            </span>
                                                            {" — "}
                                                            {w.title}
                                                            {w.detail ? ` · ${w.detail}` : ""}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : null}
                                        </details>
                                    );
                                })}
                            </div>
                        ) : null}
                    </>
                ) : (
                    <p
                        className="text-sm text-bl-muted"
                        data-testid="wcag-static-empty"
                    >
                        {scanLoading
                            ? "Loading…"
                            : "No scan yet — click \"Run scan now\" to start."}
                    </p>
                )}
            </div>
        </section>
    );
}
